import { type ReactNode, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { AdvisorModal } from './AdvisorModal'
import { AdvisorPanel } from './AdvisorPanel'
import { AlertsBell } from './AlertsBell'
import { AppSidebar } from './AppSidebar'
import { BottomNav } from './BottomNav'
import { BrandLogo } from './BrandLogo'
import { BreadcrumbBar, SetLabelContext, crumbsForPath, sectionIconForPath } from './Breadcrumbs'
import { KillSwitchBanner, KillSwitchButton } from './KillSwitch'
import { LiveFleetPill } from './LiveFleetPill'
import { PortfolioAdviceMenu } from './PortfolioAdvisor'
import { ThemeToggle } from './ThemeToggle'
import { UpdateBanner } from './UpdateBanner'
import { UserMenu } from './UserMenu'
import { AdvisorProvider, useAdvisor } from '../lib/advisor'
import { Icon } from '../lib/icons'
import { useSidebarPin } from '../lib/sidebarPin'

export function AppLayout({ children }: { children: ReactNode }) {
  return (
    <AdvisorProvider>
      <AppLayoutFrame>{children}</AppLayoutFrame>
    </AdvisorProvider>
  )
}

function AppLayoutFrame({ children }: { children: ReactNode }) {
  const advisor = useAdvisor()
  const [navOpen, setNavOpen] = useState(false)
  const [pinned, togglePin] = useSidebarPin()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  // El nombre real de un detalle (un bot, un resultado) sólo se sabe
  // después de un fetch — `useBreadcrumbLabel`, adentro de la página, lo
  // manda para acá vía `SetLabelContext`. Este estado vive en el layout, no
  // en `BreadcrumbBar`, porque el topbar (dueño del título de la pestaña)
  // es hermano de `BreadcrumbBar`, no su padre — con el estado allá abajo,
  // el topbar nunca se enteraba del nombre real y se quedaba mostrando el
  // de la URL cruda ("#2" en vez de "BTC/USDT · 5m").
  const [dynamicLabel, setDynamicLabel] = useState<string | undefined>()

  // Un cambio de ruta es la señal más confiable de que ya se navegó: cerrar
  // acá cubre tanto el click en un link (que también lo cierra directo) como
  // cualquier otra forma de llegar a una pantalla nueva con el cajón abierto.
  useEffect(() => setNavOpen(false), [pathname])

  // Ctrl/Cmd+K abre la búsqueda de Ayuda desde cualquier pantalla — a
  // propósito SIN chequear si el foco está en un input de texto: es la
  // semántica estándar de un atajo "abrir búsqueda global" (a diferencia
  // de un atajo de una sola letra, que sí tendría que respetar el foco
  // para no interrumpir lo que se está escribiendo).
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return
      event.preventDefault()
      if (pathname === '/ayuda') {
        document.getElementById('help-search-input')?.focus()
      } else {
        navigate('/ayuda?focus=1')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [pathname, navigate])

  const pageTitle = dynamicLabel ?? crumbsForPath(pathname).at(-1)?.label ?? 'Deltabot'

  return (
    <SetLabelContext.Provider value={setDynamicLabel}>
      <div
        className={`app-shell${navOpen ? ' app-shell--nav-open' : ''}${advisor.panelOpen ? ' app-shell--advisor-open' : ''}`}
      >
        <a href="#main-content" className="skip-link">
          Saltar al contenido principal
        </a>

        <AppSidebar
          brand={<BrandLogo variant="horizontal" className="app-sidebar__brand-logo" />}
          open={navOpen}
          pinned={pinned}
          onNavigate={() => setNavOpen(false)}
          onTogglePin={togglePin}
        />

        {/* Cierra el cajón al tocar afuera — sólo existe (y sólo es
            clickeable) mientras el cajón está abierto en mobile; en desktop
            el sidebar no es un overlay y esto no se monta. */}
        {navOpen && (
          <button
            className="app-nav-scrim"
            aria-label="Cerrar menú"
            onClick={() => setNavOpen(false)}
          />
        )}

        <div className="app-content">
          <header className="app-topbar">
            <h1 className="app-topbar__title">
              <Icon
                name={sectionIconForPath(pathname)}
                className="app-topbar__title-icon"
                size={24}
              />
              {pageTitle}
            </h1>

            <div className="app-topbar__ops">
              <LiveFleetPill />
              <KillSwitchButton />
              <PortfolioAdviceMenu
                onRequest={advisor.requestReading}
                isPending={advisor.isRequesting}
                advisorOpen={advisor.displayMode === 'panel' ? advisor.panelOpen : advisor.modalOpen}
                onToggleAdvisor={advisor.toggleAdvisor}
              />
              <AlertsBell />
              <ThemeToggle className="app-topbar__theme-toggle" />
              <UserMenu />
            </div>
          </header>

          <KillSwitchBanner />
          <UpdateBanner />

          {/* Siempre montada, aparte de `main` — así aparece incluso
              mientras la página está cargando o cortó con un error. */}
          <BreadcrumbBar label={dynamicLabel} />

          <main id="main-content" className="app-main" tabIndex={-1}>
            {children}
          </main>
        </div>

        {advisor.panelOpen && <AdvisorPanel />}
        {advisor.modalOpen && <AdvisorModal />}

        <BottomNav onOpenMore={() => setNavOpen(true)} />
      </div>
    </SetLabelContext.Provider>
  )
}
